import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { OverlayBackStackService } from '@newgenus/angular-shared-components/services';
import { resolveNativeBackAction } from './native-back';

/**
 * Thin bridge to the Capacitor native shell. Modelled on homeflow's
 * native-shell.service: it is a NO-OP on web / SSR / unit tests (guarded by
 * `Capacitor.isNativePlatform()`), and every plugin is **dynamically imported**
 * so the native plugin code is never pulled into the web bundle or evaluated in
 * a browser.
 *
 * Lifecycle (driven from AppComponent):
 *  - `init()` once at bootstrap: configure the status bar + wire the Android
 *    hardware back button.
 *  - `hideSplash()` after the shell has mounted and the first navigation has
 *    settled, so there's no white flash on cold start (SplashScreen is launched
 *    with `launchAutoHide:false` in capacitor.config.ts).
 */
@Injectable({ providedIn: 'root' })
export class NativeShellService {
    /** True only inside the Capacitor native runtime (Android/iOS), never on web. */
    public get isNative(): boolean {
        return Capacitor.isNativePlatform();
    }

    private initialised = false;

    /** Asked before every hardware Back — an open layer outranks history. */
    private readonly overlayBack = inject(OverlayBackStackService);

    /** Configure native chrome + back button. Safe to call on web (no-ops). */
    public async init(): Promise<void> {
        if (!this.isNative || this.initialised) {
            return;
        }
        this.initialised = true;

        try {
            const { StatusBar } = await import('@capacitor/status-bar');
            // Don't overlay the WebView. The icon style is set per-theme via
            // setStatusBarStyle() (driven by the in-app theme toggle), not here.
            await StatusBar.setOverlaysWebView({ overlay: false });
        } catch (err: unknown) {
            console.warn('[native-shell] status bar init failed', err);
        }

        try {
            const { App } = await import('@capacitor/app');
            // Android hardware back: close the top overlay if one is open, else step
            // back through history, else minimise at the root (minimise rather than
            // exit so the app/session stays warm). Capacitor's `canGoBack` misses the
            // pushState entry an overlay arms, so the overlay stack is asked first —
            // without it, Back on a restored route minimised the app with a dialog
            // still open (beta, build 32).
            App.addListener('backButton', ({ canGoBack }) => {
                if (resolveNativeBackAction(this.overlayBack.handles(), canGoBack) === 'history') {
                    window.history.back();
                } else {
                    App.minimizeApp();
                }
            });
        } catch (err: unknown) {
            console.warn('[native-shell] back-button wiring failed', err);
        }
    }

    /**
     * Match BOTH system bars (top status bar + bottom navigation bar) to the in-app
     * theme: dark theme → dark bars + light icons; light theme → light bars + dark
     * icons. Driven at runtime from `ThemeService` so it tracks the manual toggle
     * live (the @capacitor/status-bar plugin doesn't touch the nav bar — that's the
     * separate @capgo/capacitor-navigation-bar). `setStyle` uses WindowInsetsController,
     * overriding the theme's `windowLightStatusBar` on modern Android. Awaitable so
     * the caller can apply it BEHIND the splash (no visible changeover). No-op on web.
     */
    public async setSystemBars(dark: boolean): Promise<void> {
        if (!this.isNative) {
            return;
        }
        try {
            const { StatusBar, Style } = await import('@capacitor/status-bar');
            await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light });
        } catch (err: unknown) {
            console.warn('[native-shell] status bar style failed', err);
        }
        try {
            const { NavigationBar } = await import('@capgo/capacitor-navigation-bar');
            // darkButtons = dark icons on a light bar → true in light theme.
            await NavigationBar.setNavigationBarColor({ color: dark ? '#1f1f1f' : '#ffffff', darkButtons: !dark });
        } catch (err: unknown) {
            console.warn('[native-shell] navigation bar style failed', err);
        }
    }

    /** Hide the launch splash screen. Safe to call on web (no-ops). */
    public async hideSplash(): Promise<void> {
        if (!this.isNative) {
            return;
        }
        try {
            const { SplashScreen } = await import('@capacitor/splash-screen');
            await SplashScreen.hide();
        } catch (err: unknown) {
            console.warn('[native-shell] splash hide failed', err);
        }
    }
}
